import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { JsonLd } from "react-schemaorg";
import type { CollectionPage, ListItem, Person, ProfilePage } from "schema-dts";

import { config } from "shared/data";
import { useCanonicalUrl } from "shared/hooks";
import { getAbsoluteUrl } from "shared/lib/utils";

import type { authorLinkingDataAuthor$key } from "scmp-app/queries/__generated__/authorLinkingDataAuthor.graphql";

import { getSocialLinkSchema } from "./helpers";

type Props = {
  reference: authorLinkingDataAuthor$key;
};

export const AuthorLinkingData: FunctionComponent<Props> = ({ reference }) => {
  const author = useFragment(
    graphql`
      fragment authorLinkingDataAuthor on Author
      @argumentDefinitions(applicationIds: { type: "[String]", defaultValue: [] }) {
        bio
        urlAlias
        name
        image320x320: image(filter: { type: AUTHOR_IMAGE }) {
          style(filter: { style: "320x320" }) {
            url
          }
        }
        socialLinks {
          # eslint-disable-next-line relay/unused-fields -- read in ./helpers via getSocialLinkSchema
          class
          url
        }
        latestContents: contents(
          first: 20
          filter: { applicationIds: $applicationIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              urlAlias
            }
          }
        }
      }
    `,
    reference,
  );

  const { canonicalUrl } = useCanonicalUrl(author.urlAlias);

  const { email, sameAs } = useMemo(
    () => getSocialLinkSchema(author.socialLinks),
    [author.socialLinks],
  );

  const person = useMemo<Person>(
    () => ({
      "@type": "Person",
      description: author.bio ?? "",
      image: author.image320x320?.style?.url ?? "",
      name: author.name,
      sameAs,
      url: canonicalUrl,
    }),
    [author.bio, author.image320x320?.style?.url, author.name, canonicalUrl, sameAs],
  );

  const contentListItems = useMemo<ListItem[]>(
    () =>
      author.latestContents?.edges.map(({ node }, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: getAbsoluteUrl(node.urlAlias, config.general.canonicalBaseUrl),
      })) ?? [],
    [author.latestContents?.edges],
  );

  const hasPart = useMemo<CollectionPage | undefined>(
    () =>
      contentListItems.length > 0
        ? {
            "@type": "CollectionPage",
            author: person,
            mainEntity: {
              "@type": "ItemList",
              itemListElement: contentListItems,
            },
            mainEntityOfPage: canonicalUrl,
            name: author.name,
            url: canonicalUrl,
          }
        : undefined,
    [author.name, canonicalUrl, contentListItems, person],
  );

  return (
    <JsonLd<ProfilePage>
      item={{
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        hasPart,
        mainEntity: {
          ...(person as Exclude<Person, string>),
          email,
        },
        url: canonicalUrl,
      }}
    />
  );
};

AuthorLinkingData.displayName = "AuthorLinkingData";
